import { FileArchive, FileText, Film, Folder, Image, Music, Star } from 'lucide-react'
import type { ComponentType } from 'react'
import type { LucideProps } from 'lucide-react'

import { formatDuration } from '../../lib/format'
import { cx } from '../../ui'
import type { SharedAsset } from '../../portal/types'

interface Props {
  asset: SharedAsset
  canSelect: boolean
  /** Cupo agotado: la estrella se apaga en vez de dejar que falle al tocarla. */
  quotaReached: boolean
  onToggleSelect: (asset: SharedAsset) => void
  onOpen: (asset: SharedAsset) => void
}

/*
 | Iconos de verdad en lugar de emojis (🖼️ 🎬 🎵 📄 🗜️ 📁).
 |
 | Un emoji lo dibuja el sistema operativo: cambia entre Android y iPhone, no
 | hereda el color del tema —que es justo lo que rompe la marca blanca— y trae
 | su propio relleno de color que se pelea con la foto de al lado.
 */
const KIND_ICON: Record<SharedAsset['kind'], ComponentType<LucideProps>> = {
  image: Image,
  video: Film,
  audio: Music,
  document: FileText,
  archive: FileArchive,
  other: Folder,
}

export function AssetTile({ asset, canSelect, quotaReached, onToggleSelect, onOpen }: Props) {
  // Con el cupo agotado sólo se puede QUITAR lo ya elegido, para poder
  // cambiar de opinión sin tener que adivinar por qué no responde.
  const blocked = quotaReached && !asset.selected
  const KindIcon = KIND_ICON[asset.kind]

  return (
    <li className="relative">
      <button
        onClick={() => onOpen(asset)}
        // aspect-square: la retícula no debe saltar mientras cargan las
        // imágenes. Un grid que se recoloca al vuelo en móvil hace que el
        // cliente toque la foto equivocada.
        className={cx(
          'block aspect-square w-full overflow-hidden rounded-card bg-surface-sunken',
          /*
           | El marco de «elegida» va con `outline` y desplazamiento NEGATIVO,
           | no con `ring`.
           |
           | Dos razones, y la segunda sólo se ve con fotos de verdad:
           |
           | 1. Hacia dentro, porque hacia fuera se comería el hueco de 6 px
           |    entre celdas y las elegidas se tocarían con sus vecinas.
           | 2. `ring-inset` es un `box-shadow` interior, y un box-shadow se
           |    pinta DEBAJO del contenido: la imagen llena la celda entera y lo
           |    tapaba por completo. Con la retícula vacía se veía y con fotos
           |    desaparecía. `outline` se pinta por encima.
           */
          asset.selected && 'outline outline-2 outline-select-media [outline-offset:-2px]',
        )}
        // Abre el visor; NO descarga. En móvil, con 400 fotos y el pulgar,
        // el toque accidental es constante y cada descarga por error son
        // datos móviles del cliente.
        aria-label={`Ver ${asset.filename}`}
      >
        {(asset.thumb_url ?? asset.preview_url) !== null ? (
          <img
            src={asset.thumb_url ?? asset.preview_url ?? undefined}
            alt={asset.filename}
            // Miniatura de ~400 px generada por el pipeline. Si aún no
            // existe cae al original, y ahí `lazy` es lo que evita reventar los
            // datos móviles del cliente cargando 400 archivos completos.
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          // Sin derivado y sin formato pintable: un RAW o un vídeo. Se muestra
          // un marcador con el tipo en vez de intentar descargar 25 MB.
          <span className="flex h-full w-full flex-col items-center justify-center gap-2 px-2 text-ink-subtle">
            <KindIcon size={24} strokeWidth={1.5} aria-hidden />
            <span className="max-w-full truncate text-micro">{asset.filename}</span>
          </span>
        )}
        {/*
          Cuánto dura, sobre la propia miniatura.

          El dato ya venía del servidor y no lo pintaba nadie. En una entrega
          con vídeo, saber si son doce segundos o cuatro minutos decide si el
          cliente lo abre ahora o cuando llegue al wifi — que en Perú, con
          datos móviles, es la diferencia entre verlo y no verlo.

          Negro y blanco fijos y no colores del tema: esto va encima de una
          imagen, y el fondo ahí es la foto, no la superficie.
        */}
        {asset.duration_ms !== null && (
          <span
            className="absolute bottom-1.5 right-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-micro font-medium text-white backdrop-blur-sm"
            data-numeric
          >
            {formatDuration(asset.duration_ms)}
          </span>
        )}
        {/*
          Cuántas páginas, en el mismo sitio y con la misma pastilla que la
          duración: son la misma pregunta —cuánto hay aquí dentro— hecha a un
          documento en vez de a un vídeo, y de la miniatura de un PDF sólo se ve
          la portada.

          Nunca coinciden las dos: un archivo tiene duración o tiene páginas.

          Sin abreviar a «p.»: la pastilla cabe, y «12 páginas» no se puede leer
          mal. Y una sola no se pinta — un PDF de una página es una imagen con
          más pasos, y decirlo no le aporta nada a quien mira.
        */}
        {asset.pages !== null && asset.pages > 1 && (
          <span
            className="absolute bottom-1.5 right-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-micro font-medium text-white backdrop-blur-sm"
            data-numeric
          >
            {asset.pages} páginas
          </span>
        )}
      </button>

      {canSelect && (
        <button
          onClick={() => onToggleSelect(asset)}
          disabled={blocked}
          aria-pressed={asset.selected}
          aria-label={
            blocked
              ? 'Has llegado a tu número de fotos'
              : asset.selected
                ? `Quitar ${asset.filename}`
                : `Marcar ${asset.filename}`
          }
          // Esquina superior derecha: alcanzable con el pulgar sin tapar la foto.
          className={cx(
            'absolute right-1.5 top-1.5 grid h-9 w-9 place-items-center rounded-full transition-colors',
            asset.selected
              ? 'bg-select-media text-select-media-contrast shadow-raised'
              : blocked
                ? 'bg-black/25 text-white/40'
                : // Sobre una foto no se puede confiar en el tema: el fondo es la
                  // imagen, no la superficie. Aquí sí van negro y blanco fijos,
                  // con desenfoque para que se lean sobre cualquier cosa.
                  'bg-black/40 text-white backdrop-blur-sm hover:bg-black/55',
          )}
        >
          <Star
            size={17}
            strokeWidth={2}
            // Relleno cuando está elegida: a un metro de distancia lo que
            // distingue ★ de ☆ es el relleno, no el contorno.
            fill={asset.selected ? 'currentColor' : 'none'}
            aria-hidden
          />
        </button>
      )}
    </li>
  )
}
